import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon, { IconName } from '../components/Icon';
import { FeatureArt, Phone } from '../components/Phone';
import { QuickExitLink } from '../components/QuickExit';
import {
  APP_STORE_URL,
  EMERGENCY_NUMBER,
  PLAY_URL,
  SUPPORT_PHONE,
  SUPPORT_TEL,
  VIOLETA_LINES,
  VIOLETA_LINES_HOURS,
} from '../config/site';
import { SCREENS, ScreenKey } from '../config/screens';
import { detectPlatform } from '../hooks/usePlatform';
import { usePageMeta } from '../hooks/usePageMeta';

type Feature = {
  id: string;
  tab: string;
  icon: IconName;
  title: string;
  needsAccount?: boolean;
  intro: React.ReactNode;
  points: { icon: IconName; text: string }[];
  screen?: ScreenKey;
  artCaption: string;
};

// Every sentence here was checked against the app code (4.2.7) — see output/sitio fact-check, 8 Oct 2026.
const FEATURES: Feature[] = [
  {
    id: 'emergencia',
    tab: 'Botón de emergencia',
    icon: 'sos',
    title: 'Botón de emergencia',
    needsAccount: true,
    // The real emergency screen needs a login to capture; the home screen shows the same button.
    screen: 'home',
    artCaption: 'Mantén 3 segundos',
    intro: (
      <>
        Mantén presionado el botón 3 segundos. Se abre un SMS con tu ubicación para todos tus contactos de
        emergencia: tú solo tocas <b>Enviar</b>.
      </>
    ),
    points: [
      { icon: 'users', text: 'Hasta 5 contactos de emergencia' },
      { icon: 'refresh', text: 'Reenviar SMS si no salió' },
      { icon: 'chat', text: 'También por WhatsApp, a un contacto' },
    ],
  },
  {
    id: 'acompaname',
    tab: 'Acompáñame',
    icon: 'timer',
    title: 'Acompáñame',
    needsAccount: true,
    screen: 'acompaname',
    artCaption: 'Un temporizador que te cuida',
    intro: (
      <>
        Elige 15, 30, 45 o 60 minutos y cuéntale a una persona de confianza a qué hora debería saber de ti. Dos
        minutos antes del final, Violeta te pregunta «¿Estás bien?». Si el tiempo termina, prepara tu SMS de alerta.
      </>
    ),
    points: [
      { icon: 'bell', text: 'Tus contactos no reciben nada hasta que tú tocas Enviar' },
      { icon: 'plus', text: 'Puedes extender el tiempo desde la notificación o la app' },
      { icon: 'pin', text: 'Mientras está activo, tu ubicación se envía al servidor de Violeta' },
    ],
  },
  {
    id: 'directorio',
    tab: 'Directorio de servicios',
    icon: 'search',
    title: 'Directorio de servicios',
    screen: 'directory',
    artCaption: 'Servicios cerca de ti',
    intro: (
      <>
        Busca servicios de apoyo por estado y por tipo de ayuda. Hay servicios en 10 estados de Venezuela. Desde la app
        puedes llamar, escribir o ver cómo llegar.
      </>
    ),
    points: [
      { icon: 'search', text: 'Filtra por estado y por tipo de ayuda' },
      { icon: 'check', text: 'Se usa sin crear cuenta' },
    ],
  },
  {
    id: 'lineas',
    tab: 'Líneas de atención',
    icon: 'phone',
    title: 'Líneas de atención',
    screen: 'lineas',
    artCaption: 'Una llamada, a un toque',
    intro: (
      <>
        Desde la app llamas con un toque a las 4 líneas de Violeta y al <b>911</b>. La app solo abre el marcador de tu
        teléfono.
      </>
    ),
    points: [
      { icon: 'heart', text: '4 líneas de Violeta' },
      { icon: 'phone', text: '911, emergencias' },
      { icon: 'check', text: 'Se usa sin crear cuenta' },
    ],
  },
  {
    id: 'violentometro',
    tab: 'Violentómetro y Cuestionario',
    icon: 'gauge',
    title: 'Violentómetro y Cuestionario',
    screen: 'quiz',
    artCaption: 'Reconoce las señales',
    intro: (
      <>
        Responde unas preguntas y reconoce señales de violencia que a veces no se notan. El Cuestionario te enseña
        primeros auxilios psicológicos.
      </>
    ),
    points: [
      { icon: 'lock', text: 'Tus respuestas se quedan en tu teléfono' },
      { icon: 'book', text: 'Y artículos sobre violencias, derechos y prevención' },
    ],
  },
];

const FAQ: { q: string; a: React.ReactNode }[] = [
  {
    q: '¿El botón de emergencia envía el mensaje solo?',
    a: (
      <>
        No. Cuando mantienes presionado el botón 3 segundos, Violeta abre tu app de mensajes con el SMS ya escrito y
        dirigido a todos tus contactos de emergencia. Para que salga, toca <b>Enviar</b>. Si no salió, usa{' '}
        <b>Reenviar SMS</b>.
      </>
    ),
  },
  {
    q: 'Si no puedo responder, ¿Acompáñame avisa a mis contactos?',
    a: (
      <>
        No por sí sola. Cuando se acaba el tiempo, Violeta te avisa con una notificación y prepara tu SMS de alerta,
        pero el mensaje sale solo cuando tú tocas <b>Enviar</b>. Por eso, cuando actives Acompáñame, avísale también a
        una persona de confianza: dile a qué hora debería saber de ti.
      </>
    ),
  },
  {
    q: '¿Qué reciben mis contactos?',
    a: (
      <>
        Un SMS con un mensaje de alerta y un enlace de Google Maps con tu ubicación en el momento en que activaste la
        alerta. Si el teléfono no pudo saber dónde estás, el mensaje lo dice. Tus contactos no ven cómo te mueves
        después.
      </>
    ),
  },
  {
    q: '¿Necesito crear una cuenta?',
    a: (
      <>
        Solo para tres cosas: el <b>botón de emergencia</b>, <b>Acompáñame</b> y tus <b>contactos de emergencia</b>. Lo
        demás se usa sin cuenta: el Directorio, las Líneas de atención, el Violentómetro, el Cuestionario y los
        Artículos.
      </>
    ),
  },
  {
    q: '¿Violeta reemplaza al 911 o a la policía?',
    a: (
      <>
        No. Violeta te ayuda a avisar a tus personas de confianza y a encontrar apoyo, pero no avisa a ninguna central
        ni a la policía. Si estás en peligro ahora, llama al{' '}
        <a className="tap" href={`tel:${EMERGENCY_NUMBER}`}>
          {EMERGENCY_NUMBER}
        </a>
        . Denunciar o no es tu decisión.
      </>
    ),
  },
  {
    q: '¿Funciona igual en iPhone?',
    a: (
      <>
        La app de iPhone se actualiza después que la de Android, así que algunas funciones recientes pueden tardar en
        llegar. Si algo de lo que lees aquí no aparece en tu teléfono, actualiza la app desde el App Store.
      </>
    ),
  },
  {
    q: '¿Puedo borrar mi cuenta?',
    a: (
      <>
        Sí, cuando quieras, desde <b>Ajustes</b> en la app. Aquí te explicamos los pasos y qué se borra:{' '}
        <Link to="/eliminar-cuenta">Eliminar mi cuenta</Link>.
      </>
    ),
  },
];

const Home: React.FC = () => {
  usePageMeta(
    'Violeta · App de apoyo para mujeres en Venezuela',
    'Violeta es una app para mujeres en Venezuela. Prepara un mensaje con tu ubicación para tus personas de confianza (tú lo envías), busca servicios de apoyo y llama a líneas de atención.',
  );

  const platform = detectPlatform();
  const isIos = platform === 'ios';
  const storeUrl = isIos ? APP_STORE_URL : PLAY_URL;
  const storeName = isIos ? 'App Store' : 'Google Play';

  const [selected, setSelected] = useState(0);
  const feature = FEATURES[selected];
  const shot = feature.screen ? SCREENS[feature.screen] : undefined;

  const onTabKey = (event: React.KeyboardEvent, index: number) => {
    let next = -1;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % FEATURES.length;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + FEATURES.length) % FEATURES.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = FEATURES.length - 1;
    if (next < 0) return;
    event.preventDefault();
    setSelected(next);
    document.getElementById(`t-${FEATURES[next].id}`)?.focus();
  };

  return (
    <>
      {/* ============ HERO ============ */}
      <section className="hero on-dark" aria-labelledby="hero-title">
        <div className="container">
          <div className="hero__grid">
            <div className="hero__copy">
              <p className="label">Para Android y iPhone</p>
              <h1 id="hero-title">
                Tu red de apoyo,{' '}
                <span>
                  en tu{' '}
                  <span className="swash">
                    teléfono.
                    <svg viewBox="0 0 300 20" preserveAspectRatio="none" aria-hidden="true">
                      <path d="M4 13 C 70 3, 170 1, 296 9" />
                    </svg>
                  </span>
                </span>
              </h1>
              <p className="lead">
                Una app para mujeres en Venezuela. Avisa a tus personas de confianza, encuentra servicios de apoyo en 10
                estados y llama a líneas de atención.
              </p>
              <div className="hero__ctas">
                <a className="btn btn--crimson btn--lg" href={storeUrl}>
                  <Icon name={isIos ? 'apple' : 'google-play'} />
                  <span>Descargar en {storeName}</span>
                </a>
                <a className="btn btn--outline-light btn--lg" href="#como-funciona">
                  Ver cómo funciona
                </a>
              </div>
              <p className="hero__helper">
                Se abre {storeName}. ¿Ya la tienes?{' '}
                <Link className="tap" to="/actualizar">
                  Actualízala
                </Link>
              </p>
            </div>

            <div className="hero__visual">
              <span className="blob blob--big" aria-hidden="true" />
              <span className="blob blob--small" aria-hidden="true" />
              <span className="blob blob--ring" aria-hidden="true" />
              {SCREENS.home ? (
                <Phone src={SCREENS.home.src} alt={SCREENS.home.alt} />
              ) : (
                <FeatureArt icon="sos" caption="Botón de emergencia" />
              )}
              <span className="sticker sticker--a" aria-hidden="true">
                <Icon name="timer" />
                Mantén 3 segundos
              </span>
              <span className="sticker sticker--b" aria-hidden="true">
                <Icon name="check" />
                Tú tocas Enviar
              </span>
            </div>
          </div>

          <ul className="chips-row" aria-label="Lo que hace Violeta">
            <li className="chip-dark"><Icon name="sos" />Botón de emergencia</li>
            <li className="chip-dark"><Icon name="timer" />Acompáñame</li>
            <li className="chip-dark"><Icon name="search" />Directorio de servicios</li>
            <li className="chip-dark"><Icon name="phone" />Líneas de atención</li>
            <li className="chip-dark"><Icon name="gauge" />Violentómetro</li>
          </ul>
        </div>
      </section>

      {/* ============ SPOTLIGHT: EMERGENCY BUTTON ============ */}
      <section className="section spot" id="boton-de-emergencia" aria-labelledby="spot-title">
        <div className="container">
          <header className="section-head center">
            <p className="label">Botón de emergencia</p>
            <h2 className="display" id="spot-title">Pide ayuda en segundos.</h2>
            <p className="lead">El corazón de Violeta. Así funciona, paso a paso y sin sorpresas.</p>
          </header>
          <ol className="trio">
            <li>
              <span className="num" aria-hidden="true">01</span>
              <h3>Mantén presionado 3 segundos</h3>
              <p>El botón de emergencia está siempre a mano en la app.</p>
            </li>
            <li>
              <span className="num" aria-hidden="true">02</span>
              <h3>Se abre un SMS listo</h3>
              <p>Con tu ubicación de ese momento, para todos tus contactos de emergencia (hasta 5).</p>
            </li>
            <li>
              <span className="num" aria-hidden="true">03</span>
              <h3>Tú tocas Enviar</h3>
              <p>El mensaje sale cuando tú lo envías. Si no salió, usa <b>Reenviar SMS</b>.</p>
            </li>
          </ol>
          <p className="whatsapp-note">
            ¿Prefieres WhatsApp? Elige a quién escribirle. El mensaje va listo; igual tocas <b>Enviar</b>.
          </p>

          <aside className="expect" aria-labelledby="expect-title">
            <h3 id="expect-title">
              Para que sepas <em>qué esperar</em>
            </h3>
            <ul>
              <li>
                <strong>Tú envías el mensaje.</strong>
                Violeta lo deja listo, con tu ubicación, pero sale cuando tocas <b>Enviar</b>.
              </li>
              <li>
                <strong>Tu ubicación de ese momento.</strong>
                Tus contactos la reciben una vez. No ven cómo te mueves después.
              </li>
              <li>
                <strong>No es un servicio de emergencias.</strong>
                Violeta no avisa a ninguna central ni a la policía. Si estás en peligro ahora, llama al{' '}
                <a className="tap" href={`tel:${EMERGENCY_NUMBER}`}>{EMERGENCY_NUMBER}</a>.
              </li>
              <li>
                <strong>El SMS usa tu saldo.</strong>
                Y necesita señal, como cualquier mensaje.
              </li>
            </ul>
          </aside>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="section how" id="como-funciona" aria-labelledby="how-title">
        <div className="container">
          <header className="section-head">
            <p className="label">Cómo funciona</p>
            <h2 className="h2" id="how-title">Lo que puedes hacer con Violeta.</h2>
            <p className="lead">Usa solo lo que te sirva, cuando te sirva. La app no te pregunta qué te pasó.</p>
          </header>

          <div className="how__grid">
            <div className="tabs" role="tablist" aria-label="Funciones de Violeta" aria-orientation="vertical">
              {FEATURES.map((item, index) => (
                <button
                  key={item.id}
                  id={`t-${item.id}`}
                  className="tab"
                  type="button"
                  role="tab"
                  aria-selected={index === selected}
                  aria-controls={`p-${item.id}`}
                  tabIndex={index === selected ? 0 : -1}
                  onClick={() => setSelected(index)}
                  onKeyDown={(event) => onTabKey(event, index)}
                >
                  <span className="tab__num">{String(index + 1).padStart(2, '0')}</span>
                  {item.tab}
                  <Icon name="arrow-right" />
                </button>
              ))}
            </div>

            <div className="panelwrap">
              {shot ? (
                <Phone src={shot.src} alt={shot.alt} hidden />
              ) : (
                <FeatureArt icon={feature.icon} caption={feature.artCaption} />
              )}
              <div
                className="tabpanel"
                role="tabpanel"
                id={`p-${feature.id}`}
                aria-labelledby={`t-${feature.id}`}
                tabIndex={0}
              >
                <h3>{feature.title}</h3>
                {feature.needsAccount && (
                  <p className="acct">
                    <Icon name="lock" />
                    Necesitas una cuenta
                  </p>
                )}
                <p>{feature.intro}</p>
                <ul>
                  {feature.points.map((point) => (
                    <li key={point.text}>
                      <Icon name={point.icon} />
                      {point.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRIVACY ============ */}
      <section className="section on-dark" id="privacidad" aria-labelledby="privacy-title">
        <div className="container">
          <header className="section-head">
            <p className="label">Seguridad y privacidad</p>
            <h2 className="h2" id="privacy-title">Tus datos, en pocas palabras.</h2>
            <p className="lead">Tu seguridad también depende de quién ve tus datos. Esto es lo que hace Violeta con ellos.</p>
          </header>

          <div className="privacy__grid">
            <article className="pcard">
              <span className="pcard__icon"><Icon name="lock" /></span>
              <h3>Tú das los permisos</h3>
              <p>Ubicación y contactos. Puedes decir que no y agregarlos a mano.</p>
            </article>
            <article className="pcard">
              <span className="pcard__icon"><Icon name="smartphone" /></span>
              <h3>Tus contactos, en tu teléfono</h3>
              <p>No se suben a nuestro servidor. Si cierras sesión, se borran de ese teléfono.</p>
            </article>
            <article className="pcard">
              <span className="pcard__icon"><Icon name="pin" /></span>
              <h3>Ubicación, solo si tú la activas</h3>
              <p>Durante una alerta o con Acompáñame encendido. Va en el SMS a tus contactos y al servidor de Violeta.</p>
            </article>
            <article className="pcard">
              <span className="pcard__icon"><Icon name="gauge" /></span>
              <h3>Tus respuestas son tuyas</h3>
              <p>Lo que respondes en el Violentómetro y el Cuestionario no sale de tu teléfono.</p>
            </article>
          </div>

          <div className="discreet">
            <span className="pcard__icon"><Icon name="eye-off" /></span>
            <div>
              <h3>Navega con discreción</h3>
              <p>
                Si alguien se acerca, toca <b>Salir rápido</b>: esta página cambia por otra. Para borrar esta visita en
                Chrome, toca <b>⋮</b>, luego <b>Historial</b>, y la <b>X</b> junto a Violeta.
              </p>
            </div>
            <QuickExitLink className="btn btn--ink" />
          </div>

          <p className="privacy__more">
            <Link to="/politica-de-privacidad">
              Leer la política completa
              <Icon name="arrow-right" />
            </Link>
          </p>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="section" id="preguntas" aria-labelledby="faq-title">
        <div className="container">
          <header className="section-head center">
            <p className="label">Preguntas frecuentes</p>
            <h2 className="h2" id="faq-title">Sin letra pequeña.</h2>
          </header>
          <div className="faq__list">
            {FAQ.map((item, index) => (
              <details className="qa" key={item.q} open={index === 0 ? true : undefined}>
                <summary>
                  {item.q}
                  <span className="plus"><Icon name="plus" /></span>
                </summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============ DOWNLOAD ============ */}
      <section className="section download" id="descargar" aria-labelledby="dl-title">
        <div className="container download__grid">
          <div>
            <p className="label label--glass">Descargar</p>
            <h2 id="dl-title">Ten a Violeta lista antes de necesitarla.</h2>
            <ol className="steps3">
              <li><span>Descarga Violeta en Google Play o en el App Store.</span></li>
              <li><span>Crea tu cuenta y agrega hasta 5 contactos de emergencia.</span></li>
              <li>
                <span>
                  Si lo necesitas, mantén presionado el <b>botón de emergencia</b> 3 segundos y toca <b>Enviar</b>.
                </span>
              </li>
            </ol>
            <div className="stores">
              <a className="store store--light" href={PLAY_URL} rel="noopener">
                <Icon name="google-play" />
                <span><small>Descargar en</small><b>Google Play</b></span>
              </a>
              <a className="store store--light" href={APP_STORE_URL} rel="noopener">
                <Icon name="apple" />
                <span><small>Descargar en el</small><b>App Store</b></span>
              </a>
            </div>
            <div className="dl-notes">
              <p>¿Ya la tienes? <Link className="tap" to="/actualizar">Actualízala</Link></p>
              <p>Si puedes, usa wifi: así no gastas tus datos.</p>
            </div>
          </div>

          <div className="qr">
            <div className="qr__disc">
              <img
                src="/assets/qr-descargar.svg"
                width="236"
                height="236"
                alt="Código QR que abre la página de descarga de Violeta en tu teléfono"
              />
            </div>
            <span className="sticker" aria-hidden="true">Escanéame</span>
            <p>
              <strong>¿Estás en una computadora?</strong>
              Escanea el código con la cámara de tu teléfono. Abre la página de descarga para Android o iPhone.
            </p>
          </div>
        </div>
      </section>

      {/* ============ HELP NOW ============ */}
      <section className="section" id="ayuda" aria-labelledby="help-title">
        <div className="container">
          <header className="section-head">
            <p className="label">Ayuda ahora</p>
            <h2 className="h2" id="help-title">No tienes que pasar por esto sola.</h2>
          </header>
          <div className="help__grid">
            <div className="help-card help-card--urgent">
              <h3>Si estás en peligro</h3>
              <p className="big">{EMERGENCY_NUMBER}</p>
              <p>Número de emergencias.</p>
              <a className="btn btn--light" href={`tel:${EMERGENCY_NUMBER}`}>
                <Icon name="phone" />
                Llamar al {EMERGENCY_NUMBER}
              </a>
            </div>

            <div className="help-card help-card--lilac">
              <h3>Líneas de Violeta</h3>
              <ul className="nums">
                {VIOLETA_LINES.map((line) => (
                  <li key={line.tel}>
                    <a href={`tel:${line.tel}`} aria-label={`Llamar al ${line.label}`}>
                      {line.label}
                      <Icon name="phone" />
                    </a>
                  </li>
                ))}
              </ul>
              <p>
                <b>Atienden {VIOLETA_LINES_HOURS}</b> Fuera de ese horario, llama al {EMERGENCY_NUMBER}. Si una no
                contesta, prueba con otra.
              </p>
            </div>

            <div className="help-card">
              <h3>Teléfono de la app</h3>
              <p className="big">{SUPPORT_PHONE}</p>
              <p>Para dudas sobre la app. No es una línea de emergencias.</p>
              <a className="btn btn--outline" href={SUPPORT_TEL}>
                <Icon name="phone" />
                Llamar
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
